import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, Tooltip
} from "recharts";
import { ArrowLeft, Save, Sparkles, FileText, Package, Plus, Trash2, ChevronDown, ChevronRight, Send, User as UserIcon, Lock, Clock, Camera, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { WaiveAssessmentButton } from "@/components/WaiveAssessmentButton";
import { AtRiskBookingsPanel } from "@/components/AtRiskBookingsPanel";
import { useSport } from "@/context/SportContext";
import { useAuth } from "@/context/AuthContext";
import { buildAvatarUrl } from "@/components/ProfileImageUpload";
import SportSwitcher from "@/components/SportSwitcher";
import CoachAssessmentPanel from "@/components/CoachAssessmentPanel";
import CoachNotesTimeline from "@/components/CoachNotesTimeline";
import RecommendedDrills from "@/components/RecommendedDrills";
import VoiceNoteRecorder from "@/components/VoiceNoteRecorder";
import SkillSnapshotRadar from "@/components/SkillSnapshotRadar";
import RecapHistory from "@/components/RecapHistory";
import MilestonesCard from "@/components/MilestonesCard";
import EditorialProgressMap from "@/components/EditorialProgressMap";
import ClaimHistoryBreadcrumb from "@/components/ClaimHistoryBreadcrumb";
import PostSessionFeedbackModal from "@/components/PostSessionFeedbackModal";
import FeedbackHistoryPanel from "@/components/FeedbackHistoryPanel";
import { NoShowLog, RequestAssessmentButton } from "@/components/NoShowLog";
import PurchaseHistory from "@/components/PurchaseHistory";
import { ReCreditButton } from "@/components/ReCreditButton";

const SUBSKILL_MIN_LEVEL = 4.0;

export default function PlayerDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { skills, sport, setSport, meta } = useSport();
  const isAdmin = user?.permissions?.includes("admin") || user?.permissions?.includes("super_admin") || user?.role === "admin";
  const [player, setPlayer] = useState(null);
  const [history, setHistory] = useState([]);
  const [ratings, setRatings] = useState({});
  const [subRatings, setSubRatings] = useState({}); // {parent: {subId: 1-10}}
  const [focus, setFocus] = useState("");
  const [note, setNote] = useState("");
  const [report, setReport] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [sendingFocus, setSendingFocus] = useState(false);
  const [suggestingFocus, setSuggestingFocus] = useState(false);
  // Iter105 (Slice B) — remember the last AI suggestion so we can pass it
  // back to /focus on save. Cleared once the coach hits Save (post-persist).
  const [aiSuggestedFocus, setAiSuggestedFocus] = useState(null);
  const [reports, setReports] = useState([]);
  const [gating, setGating] = useState(null);
  const [packages, setPackages] = useState([]);
  // Sub-skills (drill-down for 4.0+ players)
  const [subEnabled, setSubEnabled] = useState(false);  // coach-wide preference
  const [templates, setTemplates] = useState([]);
  const [customs, setCustoms] = useState([]);
  const [expanded, setExpanded] = useState({});  // {parent_skill: bool}
  const [newRow, setNewRow] = useState({});  // {parent_skill: string}
  // Skill caps & criteria — power the "are you sure?" guardrails.
  const [caps, setCaps] = useState({});  // {Serve: 7, ...}
  const [criteria, setCriteria] = useState({});  // {Serve: [{name, criteria, group}, ...]}
  const [openCriteria, setOpenCriteria] = useState({}); // {skill: bool}
  const [capModal, setCapModal] = useState(null); // null | { breaches: [{skill,rating,cap}], onConfirm }
  // Feedback-availability gate for the two-step "Mark attended → Mark feedback
  // delivered" flow. True when the coach has saved at least one coach note OR
  // sent at least one session recap for this player — unlocks the second button.
  const [feedbackAvailable, setFeedbackAvailable] = useState(false);
  // Iter155 — Sessions this coach still owes feedback on for THIS player.
  // Powers the "Sessions requiring feedback" card below the assessment header.
  const [pendingFeedback, setPendingFeedback] = useState([]);
  const [feedbackTarget, setFeedbackTarget] = useState(null);
  // Iter172 — Coach/admin snaps a photo of the player during assessment and
  // it becomes their profile photo. `capture="environment"` opens the rear
  // camera on mobile Safari/Chrome; desktop falls back to file picker.
  const [avatarUploading, setAvatarUploading] = useState(false);
  const playerPhotoRef = useRef(null);
  // Iter174 — Show the AI moderator's rejection reason inside a modal so
  // coaches can read it and hit "Try another photo" without hunting for the
  // hidden file input.
  const [photoRejection, setPhotoRejection] = useState(null); // {reason: string}

  const uploadPlayerPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Photo is too large (max 8MB).");
      if (playerPhotoRef.current) playerPhotoRef.current.value = "";
      return;
    }
    setAvatarUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/coach/players/${id}/avatar`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      setPlayer((cur) => cur ? { ...cur, avatar_path: data.avatar_path } : cur);
      setPhotoRejection(null);
      toast.success("Player photo saved");
    } catch (err) {
      const status = err?.response?.status;
      const detail = formatApiError(err?.response?.data?.detail) || "Upload failed";
      if (status === 400) {
        // Vision moderator said no — surface the reason in-modal with a
        // Retake button instead of a fleeting toast.
        setPhotoRejection({ reason: detail });
      } else {
        toast.error(detail);
      }
    } finally {
      setAvatarUploading(false);
      if (playerPhotoRef.current) playerPhotoRef.current.value = "";
    }
  };

  const loadPendingFeedback = async () => {
    try {
      const { data } = await api.get("/me/coach/pending-feedback");
      const items = (data?.items || []).filter((it) => it.student_id === id);
      setPendingFeedback(items);
    } catch { /* silent — non-coach users get 403 */ }
  };

  const load = async () => {
    // Iter102b — Each background call is wrapped in .catch() so one failing
    // endpoint (e.g. a coach with global access hitting a rating endpoint
    // that's still 403-gated for edge cases) can't take the whole profile
    // page down. `/students/${id}` is the only truly required call — if that
    // fails we still let the outer error bubble so React shows a proper
    // error state instead of half-rendering with undefined `player`.
    const safe = (p) => p.catch(() => ({ data: null }));
    const [p, h, l, f, rep, g, pks, prefs, tpls, csts, notes, recaps] = await Promise.all([
      api.get(`/students/${id}`),                                           // required
      safe(api.get(`/skills/${id}?sport=${sport}`)),
      safe(api.get(`/skills/${id}/latest?sport=${sport}`)),
      safe(api.get(`/focus/${id}?sport=${sport}`)),
      safe(api.get(`/reports/${id}?sport=${sport}`)),
      safe(api.get(`/reports/${id}/gating?sport=${sport}`)),
      safe(api.get(`/packages?student_id=${id}`)),
      safe(api.get(`/coach/subskill-prefs?sport=${sport}`)),
      safe(api.get(`/subskills/templates?sport=${sport}`)),
      safe(api.get(`/subskills/customs?student_id=${id}&sport=${sport}`)),
      api.get(`/students/${id}/coach-notes`).catch(() => ({ data: [] })),
      api.get(`/coach/players/${id}/recaps?limit=1`).catch(() => ({ data: [] })),
    ]);
    setPlayer(p.data);
    setHistory(h.data || []);
    const seed = Object.fromEntries(((l.data && l.data.skills) || skills).map((s) => [s, 5]));
    setRatings({ ...seed, ...((l.data && l.data.ratings) || {}) });
    setSubRatings((l.data && l.data.sub_ratings) || {});
    setFocus((f.data && f.data.focus) || "");
    setReports(rep.data);
    setGating(g.data);
    setPackages((pks.data) || []);
    setSubEnabled(Boolean(prefs.data && prefs.data.enabled));
    setTemplates(tpls.data || []);
    setCustoms(csts.data || []);
    setReport(null);
    setFeedbackAvailable((notes.data?.length || 0) > 0 || (recaps.data?.length || 0) > 0);
    // Fetch caps + criteria for the player's current level so the slider
    // guardrails know when to warn. These depend on player.assessment_level.current
    // so we run them AFTER setPlayer.
    const level = p.data?.assessment_level?.current;
    if (level) {
      try {
        const [capsRes, critRes] = await Promise.all([
          api.get(`/skill-caps?sport=${sport}&level=${level}`),
          api.get(`/level-criteria?sport=${sport}&level=${level}`),
        ]);
        setCaps(capsRes.data?.caps || {});
        setCriteria(critRes.data || {});
      } catch (_e) { /* swallow */ }
    }
  };
  useEffect(() => {
    if (skills.length) load().catch((e) => toast.error(formatApiError(e?.response?.data?.detail) || "You don't have access to this player"));
  }, [id, sport, skills.length]);
  useEffect(() => {
    if (user?.role === "coach" || user?.role === "admin" ||
        (user?.permissions || []).some((p) => p === "admin" || p === "super_admin")) {
      loadPendingFeedback();
    }
  }, [id, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll to the assessment section on landing when either:
  //   · the URL was hash-linked (#assessment) — e.g. from the Players list
  //   · the player is awaiting_feedback (coach owes them a rating right now)
  // Runs once the player doc has loaded so the anchor is actually in the DOM.
  useEffect(() => {
    if (!player) return;
    const hasHash = typeof window !== "undefined" && window.location.hash === "#assessment";
    const needsFeedback = player.assessment_status === "awaiting_feedback"
                          || player.assessment_status === "scheduled";
    if (!hasHash && !needsFeedback) return;
    // Slight delay so any lazy-mounted content below the fold is present.
    const t = setTimeout(() => {
      const el = document.getElementById("assessment");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
    return () => clearTimeout(t);
     
  }, [player?.id, player?.assessment_status]);

  // Identify any current rating that exceeds the cap for the player's level.
  const findCapBreaches = () => {
    const out = [];
    for (const s of skills) {
      const r = Number(ratings[s] || 0);
      const cap = Number(caps[s] || 10);
      if (r > cap) out.push({ skill: s, rating: r, cap });
    }
    return out;
  };

  const saveRating = async () => {
    // iter52: sliders retired. This just saves session focus + internal note
    // now — the actual per-skill ratings come from the DUPR checklist below.
    await doSave();
  };

  const doSave = async () => {
    try {
      await api.post("/focus", { student_id: id, sport, focus, ai_suggested_focus: aiSuggestedFocus });
      // Session note (internal): still posted to /skills for legacy history,
      // but without ratings so we don't blast default-5s into the collection.
      if ((note || "").trim()) {
        await api.post("/students/" + id + "/coach-notes", { body: note, context: "session" });
      }
      toast.success(`Session focus saved`);
      setNote("");
      setAiSuggestedFocus(null);  // pair captured — reset so future edits don't double-log
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendFocusToPlayer = async () => {
    if (!focus.trim()) {
      toast.error("Nothing to send — add drills or type a plan first.");
      return;
    }
    setSendingFocus(true);
    try {
      // Persist any unsaved edits before emailing so the player gets exactly
      // what the coach sees.
      await api.post("/focus", { student_id: id, sport, focus, ai_suggested_focus: aiSuggestedFocus });
      const res = await api.post(`/focus/${id}/send?sport=${sport}`);
      toast.success(`Session plan emailed to ${res.data?.sent_to || "player"}`);
      setAiSuggestedFocus(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send plan");
    } finally {
      setSendingFocus(false);
    }
  };

  const suggestFocus = async () => {
    // Iter105 (Slice B) — Confirm before overwriting existing coach edits.
    if ((focus || "").trim() && !window.confirm("Replace the current focus with an AI suggestion? Your existing text will be overwritten.")) return;
    setSuggestingFocus(true);
    try {
      const { data } = await api.post(`/focus/${id}/suggest?sport=${sport}`);
      const suggestion = (data?.focus || "").trim();
      if (!suggestion) { toast.error("No suggestion returned"); return; }
      setFocus(suggestion);
      setAiSuggestedFocus(suggestion);
      toast.success(`Draft ready · ${data.used_notes || 0} notes · ${data.used_examples || 0} style examples`, { duration: 5000 });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Suggestion failed");
    } finally { setSuggestingFocus(false); }
  };

  const toggleSubEnabled = async () => {    try {
      const next = !subEnabled;
      await api.put("/coach/subskill-prefs", { sport, enabled: next });
      setSubEnabled(next);
      toast.success(next ? "Sub-skill rating turned ON" : "Sub-skill rating turned OFF");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const addCustomRow = async (parentSkill) => {
    const name = (newRow[parentSkill] || "").trim();
    if (!name) { toast.error("Enter a sub-skill name"); return; }
    try {
      const { data } = await api.post("/subskills/customs", { student_id: id, sport, parent_skill: parentSkill, name });
      setCustoms((cur) => [...cur, data]);
      setNewRow((cur) => ({ ...cur, [parentSkill]: "" }));
      toast.success("Sub-skill added");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const removeCustomRow = async (custom) => {
    if (!window.confirm(`Remove "${custom.name}" sub-skill?`)) return;
    try {
      await api.delete(`/subskills/customs/${custom.id}`);
      setCustoms((cur) => cur.filter((c) => c.id !== custom.id));
      // Also drop the rating
      setSubRatings((cur) => {
        const next = { ...cur };
        if (next[custom.parent_skill]) {
          const inner = { ...next[custom.parent_skill] };
          delete inner[custom.id];
          next[custom.parent_skill] = inner;
        }
        return next;
      });
      toast.success("Removed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const removeTemplateRow = async (tpl) => {
    if (!window.confirm(`Remove "${tpl.name}" sub-skill ${tpl.scope === "global" ? "(GLOBAL — affects all coaches)" : "(personal)"}?`)) return;
    try {
      await api.delete(`/subskills/templates/${tpl.id}`);
      setTemplates((cur) => cur.filter((t) => t.id !== tpl.id));
      toast.success("Removed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const setSubRating = (parentSkill, subId, value) => {
    setSubRatings((cur) => ({
      ...cur,
      [parentSkill]: { ...(cur[parentSkill] || {}), [subId]: value },
    }));
  };

  const generateReport = async (force = false) => {
    setGenerating(true);
    try {
      const { data } = await api.post("/reports/generate", { student_id: id, sport, force }, { timeout: 120000 });
      setReport(data);
      toast.success(`${meta.label} report generated${force ? " (admin override)" : ""}`);
      const [list, g] = await Promise.all([
        api.get(`/reports/${id}?sport=${sport}`),
        api.get(`/reports/${id}/gating?sport=${sport}`),
      ]);
      setReports(list.data);
      setGating(g.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setGenerating(false); }
  };

  if (!player) return <div className="label-mono text-[#A3A3A3]">Loading…</div>;

  const radarData = skills.map((s) => ({ skill: s, value: ratings[s] || 0 }));
  const historyChart = history.map((h) => {
    const vals = Object.values(h.ratings || {}).filter((v) => typeof v === "number");
    return { date: (h.created_at || "").slice(5, 10), avg: vals.length ? +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : 0 };
  });

  return (
    <div className="space-y-6">
      <Link to="/players" className="inline-flex items-center gap-2 text-xs text-[#A3A3A3] hover:text-white uppercase tracking-wider" data-testid="back-players">
        <ArrowLeft className="w-3.5 h-3.5" /> Players
      </Link>

      {/* Iter101 — Public Snapshot lock. Backend flags `snapshot_only:true`
          when the viewing coach isn't on the player's allowed_coach_ids
          whitelist. Show a minimal card + Request Full Access button; don't
          render the rest of the profile. */}
      {player.snapshot_only && <PlayerSnapshotLock player={player} />}
      {player.snapshot_only && null}

      {!player.snapshot_only && (<>

      <div className="flex items-end justify-between flex-wrap gap-4 fade-up">
        <div className="flex items-end gap-4">
          {/* Iter79: Coach sees player avatar on the profile header.
              Iter172: Coach/admin can also snap or upload a photo during
              assessment — opens the rear camera on mobile via capture="environment". */}
          <div className="flex flex-col items-center gap-2">
            <div className="relative">
              {player.avatar_path ? (
                <img
                  src={buildAvatarUrl(player.avatar_path)}
                  alt={`${player.name}'s profile`}
                  className="w-20 h-20 rounded-full object-cover border border-[#262626]"
                  data-testid="player-avatar"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-[#141414] border border-[#262626] flex items-center justify-center text-[#525252]" data-testid="player-avatar-placeholder">
                  <UserIcon className="w-8 h-8" strokeWidth={1.25} />
                </div>
              )}
              {avatarUploading && (
                <div className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center">
                  <Loader2 className="w-5 h-5 animate-spin text-[#CCFF00]" />
                </div>
              )}
            </div>
            {(user?.role === "coach" || user?.role === "admin" ||
              (user?.permissions || []).some((p) => p === "admin" || p === "super_admin")) && (
              <>
                <button
                  type="button"
                  onClick={() => playerPhotoRef.current?.click()}
                  disabled={avatarUploading}
                  data-testid="player-photo-take"
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00] px-2 py-1 disabled:opacity-50"
                  title="Snap or upload a photo of this player"
                >
                  <Camera className="w-3 h-3" strokeWidth={2} />
                  {player.avatar_path ? "Change" : "Take photo"}
                </button>
                <div
                  className="text-[9px] text-[#525252] leading-tight max-w-[110px] text-center"
                  data-testid="player-photo-hint"
                >
                  Head & shoulders, good light. Blurry or obscured shots get rejected.
                </div>
                <input
                  ref={playerPhotoRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  capture="environment"
                  onChange={uploadPlayerPhoto}
                  className="hidden"
                  data-testid="player-photo-input"
                />
              </>
            )}
          </div>

          {/* Iter174 — Photo rejection modal. Shows the AI moderator's reason
              and a Retake button so coaches can try again without hunting
              for the hidden file input. */}
          {photoRejection && (
            <div
              className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4"
              role="dialog"
              aria-modal="true"
              data-testid="photo-rejection-modal"
              onClick={() => setPhotoRejection(null)}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md bg-[#0A0A0A] border border-[#FF4500] p-6 relative"
              >
                <div className="flex items-start gap-3 mb-3">
                  <AlertTriangle className="w-5 h-5 text-[#FF4500] shrink-0 mt-0.5" />
                  <div>
                    <div className="label-mono text-[#FF4500] mb-1">PHOTO REJECTED</div>
                    <h2 className="h-display text-xl">Try another shot</h2>
                  </div>
                </div>
                <p
                  className="text-sm text-[#E5E5E5] whitespace-pre-wrap border border-[#262626] bg-[#141414] p-3 mb-3"
                  data-testid="photo-rejection-reason"
                >
                  {photoRejection.reason}
                </p>
                <p className="text-[10px] text-[#525252] mb-4 leading-relaxed">
                  Tip: face fills roughly 1/3 of the frame, eyes visible, no heavy filter or backlight. A quick head-and-shoulders selfie usually works first time.
                </p>
                <div className="flex justify-end gap-2 pt-3 border-t border-[#262626]">
                  <button
                    type="button"
                    onClick={() => setPhotoRejection(null)}
                    className="px-4 py-2 text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white"
                    data-testid="photo-rejection-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => { setPhotoRejection(null); playerPhotoRef.current?.click(); }}
                    data-testid="photo-rejection-retake"
                    className="btn-volt px-4 py-2 text-xs inline-flex items-center gap-1.5"
                    style={{ background: "#CCFF00", color: "#0A0A0A" }}
                  >
                    <Camera className="w-3.5 h-3.5" />
                    Try another photo
                  </button>
                </div>
              </div>
            </div>
          )}
          <div>
          <div className="label-mono mb-2">PLAYER PROFILE</div>
          <h1 className="h-display text-5xl tracking-tighter">{player.name}</h1>
          <div className="text-sm text-[#A3A3A3] mt-1">{player.email}</div>
          {player.dupr_rating && <div className="text-sm mt-1" data-testid="player-dupr-rating"><span className="label-mono text-[#A3A3A3]">DUPR</span> <span className="font-semibold text-white">{player.dupr_rating}</span></div>}
          {user?.role !== "student" && /^\d+(\.\d+)?$/.test((player.dupr_rating || "").trim()) && player.assessment_level?.current
            && parseFloat(player.dupr_rating) > parseFloat(player.assessment_level.current) && (
            <div className="text-xs text-[#66C2FF] mt-1" data-testid="player-dupr-above-target">
              DUPR {player.dupr_rating.trim()} — above target level {player.assessment_level.current}
            </div>
          )}
          <PlayerContactDetails pd={player.personal_details} />
          <div className="mt-2"><ReCreditButton studentId={player.id} studentName={player.name} testid="player-recredit" /></div>
          {player.media_consent?.status === "declined" && (
            <div
              className="mt-3 flex items-center gap-2 px-3 py-2 border-l-4"
              style={{ background: "rgba(255,69,0,0.08)", borderColor: "#FF4500" }}
              data-testid="player-media-consent-declined-banner"
            >
              <span className="label-mono" style={{ color: "#FF4500", fontWeight: 700 }}>
                🚫 NO MEDIA CONSENT
              </span>
              <span className="text-xs text-[#E5E5E5]">
                This player has opted out of photos/videos. Do not record any part of their sessions.
              </span>
            </div>
          )}
          {player.assessment_status && player.assessment_status !== "completed" && (
            <div className="mt-4"><AtRiskBookingsPanel studentId={player.id} studentName={player.name} /></div>
          )}
          {player.assessment_status && (
            <div className="mt-2 inline-flex items-center gap-2 flex-wrap">
              <span className="label-mono px-2 py-1" style={{
                background: player.assessment_status === "completed"
                  ? "#CCFF00"
                  : player.assessment_status === "awaiting_feedback"
                    ? "#FFD700"      // amber — coach action needed
                    : player.assessment_status === "scheduled"
                      ? "#66C2FF"    // blue — booked, not yet due
                      : "#FF4500",  // required — orange
                color: "#0A0A0A", fontWeight: 700,
              }}>
                ASSESSMENT · {player.assessment_status === "awaiting_feedback" && player.assessment_attended_at
                  ? "ATTENDED · FEEDBACK PENDING"
                  : (player.assessment_status || "").replace(/_/g, " ").toUpperCase()}{player.assessment_waived ? " (WAIVED)" : ""}
              </span>
              {player.assessment_status === "awaiting_feedback" && (
                <span className="text-[10px] text-[#FFD700] italic" data-testid="assessment-awaiting-hint">
                  {player.assessment_attended_at
                    ? "Attended · save a coach note or send a recap to complete"
                    : "Session date passed — click Mark attended"}
                </span>
              )}
              {player.assessment_waived && player.assessment_waived_at && (
                <span className="text-xs text-[#A3A3A3]" data-testid="assessment-waiver-meta">
                  Waived by {player.assessment_waived_by_name || "coach"} on {new Date(player.assessment_waived_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                </span>
              )}
              {player.assessment_waived && player.assessment_waived_reason && (
                <span className="text-xs text-[#A3A3A3] italic" data-testid="assessment-waiver-reason">
                  &ldquo;{player.assessment_waived_reason}&rdquo;
                </span>
              )}
            </div>
          )}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Two-step attendance flow:
              · scheduled OR (awaiting_feedback without attended stamp) → Mark attended
              · awaiting_feedback WITH attended stamp → Mark feedback delivered
                (button disabled until a coach note or recap exists) */}
          {(player.assessment_status === "scheduled"
            || (player.assessment_status === "awaiting_feedback" && !player.assessment_attended_at))
            && player.assessment_booking_id && (
            <button
              data-testid="mark-attended"
              onClick={async () => {
                try {
                  await api.post(`/coach/assessments/${player.assessment_booking_id}/attended`);
                  toast.success("Marked attended — coach owes feedback");
                  const r = await api.get(`/students/${id}`);
                  setPlayer(r.data);
                } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
              }}
              className="px-5 py-3 text-xs inline-flex items-center gap-2 font-bold uppercase tracking-wider"
              style={{ background: "#FF4500", color: "#0A0A0A" }}
            >
              ✓ Mark attended
            </button>
          )}

          {player.assessment_status === "awaiting_feedback"
            && player.assessment_attended_at
            && player.assessment_booking_id && (
            <button
              data-testid="mark-feedback-delivered"
              disabled={!feedbackAvailable}
              title={feedbackAvailable ? "" : "Save at least one coach note or send a session recap to unlock this."}
              onClick={async () => {
                try {
                  await api.post(`/coach/assessments/${player.assessment_booking_id}/complete`);
                  toast.success("Feedback delivered — player now has full tier access");
                  await load();
                } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
              }}
              className="px-5 py-3 text-xs inline-flex items-center gap-2 font-bold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: feedbackAvailable ? "#CCFF00" : "#525252", color: "#0A0A0A" }}
            >
              ✓ Mark feedback delivered
            </button>
          )}

          {/* Manual override: complete or waive (no booking required) */}
          {player.assessment_status !== "completed" && (
            <>
              {player.assessment_status !== "awaiting_feedback" && <RequestAssessmentButton player={player} onDone={load} />}
              <button
                data-testid="assessment-mark-complete"
                onClick={async () => {
                  if (!window.confirm(`Mark ${player.name}'s assessment as completed?\n\nUse this when you've assessed them in person without an in-app booking.`)) return;
                  try {
                    await api.post(`/coach/players/${id}/assessment-override`, { action: "complete" });
                    toast.success("Assessment marked complete");
                    const r = await api.get(`/students/${id}`);
                    setPlayer(r.data);
                  } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
                }}
                className="px-4 py-2.5 text-xs inline-flex items-center gap-2 font-bold uppercase tracking-wider border"
                style={{ borderColor: "#CCFF00", color: "#CCFF00" }}
              >
                ✓ Mark as completed
              </button>
              <WaiveAssessmentButton player={player} onDone={setPlayer} />
            </>
          )}

          {/* If already completed/waived, allow reset */}
          {player.assessment_status === "completed" && (
            <button
              data-testid="assessment-reset"
              onClick={async () => {
                if (!window.confirm(`Reset ${player.name}'s assessment status?\n\nThey'll need to complete (or be waived from) the assessment again before advanced bookings.`)) return;
                try {
                  await api.post(`/coach/players/${id}/assessment-override`, { action: "reset" });
                  toast.success("Assessment status reset to 'required'");
                  const r = await api.get(`/students/${id}`);
                  setPlayer(r.data);
                } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
              }}
              className="px-3 py-2 text-[10px] inline-flex items-center gap-1 uppercase tracking-wider border border-[#262626] text-[#525252] hover:text-[#A3A3A3]"
            >
              Reset
            </button>
          )}
          <SportSwitcher sport={sport} onChange={setSport} />
          {gating && (() => {
            const canManual = gating.can_generate_manually;
            const sinceLast = gating.sessions_since_last || 0;
            const remaining = Math.max(0, 3 - sinceLast);
            const isAdmin = gating.is_admin;
            const disabled = generating || (!canManual && !isAdmin);
            const buttonLabel = generating
              ? "Generating…"
              : canManual
                ? (sinceLast >= 4 ? "Generate AI report (4+ sessions)" : "Generate AI report")
                : isAdmin
                  ? `Generate (admin override · ${remaining} session${remaining === 1 ? "" : "s"} needed)`
                  : `${remaining} more session${remaining === 1 ? "" : "s"} needed`;
            return (
              <div className="flex flex-col items-end gap-1">
                <button
                  data-testid="generate-report"
                  onClick={() => generateReport(!canManual && isAdmin)}
                  disabled={disabled}
                  className="btn-volt px-5 py-3 text-xs inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={canManual ? { background: "#CCFF00", color: "#0A0A0A" } : {}}
                >
                  <Sparkles className="w-4 h-4" /> {buttonLabel}
                </button>
                <div className="text-[10px] text-[#A3A3A3]" data-testid="report-gating-info">
                  {sinceLast}/3 Performance &amp; 1:1 sessions since last report
                  {gating.auto_eligible ? " · auto-generates on next rating" : ""}
                </div>
              </div>
            );
          })()}
          {!gating && (
            <button data-testid="generate-report" onClick={() => generateReport(false)} disabled={generating}
              className="btn-volt px-5 py-3 text-xs inline-flex items-center gap-2 disabled:opacity-50">
              <Sparkles className="w-4 h-4" /> {generating ? "Generating…" : "Generate AI report"}
            </button>
          )}
        </div>
      </div>

      {/* Iter155 — Sessions requiring feedback (for this player). Renders
          only when the current coach owes feedback for one or more past
          sessions with this specific player. Each row opens the 2-step
          PostSessionFeedbackModal. */}
      {pendingFeedback.length > 0 && (
        <div className="card-surface p-6 border-l-4 border-[#FF4500] fade-up" data-testid="player-pending-feedback">
          <div className="flex items-center gap-2 mb-3">
            <span className="label-mono text-[#FF4500]">FEEDBACK NEEDED · {pendingFeedback.length}</span>
          </div>
          <p className="text-xs text-[#A3A3A3] mb-4">
            You still owe {player?.name || "this player"} a post-session recap for the sessions below.
          </p>
          <div className="space-y-2">
            {pendingFeedback.map((it) => (
              <div
                key={`pf-${it.booking_id}`}
                data-testid={`pending-feedback-row-${it.booking_id}`}
                className="bg-[#0A0A0A] border border-[#262626] p-3 flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <div className="text-sm truncate">{it.title}</div>
                  <div className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">
                    {it.session_start ? new Date(it.session_start).toLocaleString(undefined, {weekday:"short",day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"}) : "—"}
                    {" · "}{it.duration_min || 60}min
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setFeedbackTarget(it)}
                  data-testid={`open-feedback-modal-${it.booking_id}`}
                  className="px-3 py-2 text-xs uppercase tracking-wider shrink-0"
                  style={{ background: "#CCFF00", color: "#0A0A0A" }}
                >
                  Add feedback →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {feedbackTarget && (
        <PostSessionFeedbackModal
          open={!!feedbackTarget}
          onClose={() => setFeedbackTarget(null)}
          onSaved={() => { loadPendingFeedback(); load(); }}
          bookingId={feedbackTarget.booking_id}
          studentId={id}
          studentName={player?.name || ""}
          sessionTitle={feedbackTarget.title}
          sport={feedbackTarget.sport || sport}
        />
      )}

      {/* Iter102 — Privacy & Access card. Admin-only for now (Global Access
          toggle + Grant Access override live here). Shows Restrict Sharing
          state; toggle is enabled for admins. Non-admin coaches don't see
          this card at all. */}
      {(user?.role === "admin" || (user?.permissions || []).some((p) => ["admin", "super_admin"].includes(p))) && (
        <PrivacyAccessCard player={player} onChanged={async () => {
          const r = await api.get(`/students/${id}`);
          setPlayer(r.data);
        }} />
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Rate skills */}
        <div className="card-surface p-6 fade-up d1">
          {/* Rate skills 1-10 UI retired (iter52) — the radar on the right
              is now DUPR-derived from the CoachAssessmentPanel ticks. This
              card keeps the coach-facing "next focus" + session note controls. */}
          <div className="label-mono mb-4">Session focus</div>
          <div className="mt-5 space-y-3">
            <div>
              <div className="flex items-center justify-between mb-1 gap-2">
                <div className="label-mono">Next {meta.label.toLowerCase()} session focus</div>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={suggestFocus}
                    disabled={suggestingFocus}
                    data-testid="suggest-focus-btn"
                    className="text-[10px] uppercase tracking-wider label-mono px-2 py-1 border inline-flex items-center gap-1 transition-colors border-[#404040] text-[#A3A3A3] hover:text-[#CCFF00] hover:border-[#CCFF00] disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Draft with Claude using recent notes + your editing style"
                  >
                    <Sparkles className="w-3 h-3" />
                    {suggestingFocus ? "Drafting…" : "Suggest with AI"}
                  </button>
                  <button
                    type="button"
                    onClick={sendFocusToPlayer}
                    disabled={sendingFocus || !focus.trim()}
                    data-testid="send-focus-btn"
                    className="text-[10px] uppercase tracking-wider label-mono px-2 py-1 border inline-flex items-center gap-1 transition-colors border-[#404040] text-[#A3A3A3] hover:text-[#CCFF00] hover:border-[#CCFF00] disabled:opacity-40 disabled:hover:text-[#A3A3A3] disabled:hover:border-[#404040] disabled:cursor-not-allowed"
                    title={focus.trim() ? "Email this plan to the player" : "Add drills or notes before sending"}
                  >
                    <Send className="w-3 h-3" />
                    {sendingFocus ? "Sending…" : "Send to player"}
                  </button>
                </div>
              </div>
              <textarea value={focus} onChange={(e) => setFocus(e.target.value)} rows={4} data-testid="focus-input"
                placeholder={sport === "squash" ? "e.g., Length & boasts to the back" : "e.g., Third shot drops & dink resets"}
                className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-[#CCFF00] resize-y" />
              {aiSuggestedFocus && (
                <div className="mt-1.5 flex items-center gap-2 text-[10px] text-[#CCFF00] uppercase tracking-wider" data-testid="ai-suggestion-active">
                  <Sparkles className="w-3 h-3" />
                  AI seeded — your edits train future suggestions
                </div>
              )}
            </div>
            <div>
              <div className="label-mono mb-1">Session note (internal)</div>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="note-input"
                className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-[#CCFF00]" />
            </div>
            <button onClick={saveRating} className="btn-volt w-full py-3 text-sm inline-flex items-center justify-center gap-2" data-testid="save-rating"
              style={{ background: meta.color }}>
              <Save className="w-4 h-4" /> Save session focus
            </button>
          </div>
        </div>

        {/* Radar — DUPR-derived (iter52): rings 2.5 → 5.5 in 0.25 increments,
            polygon rendered from assessment_level.skills ticks, no separate
            rating step. Old 0-10 radar retired. */}
        <SkillSnapshotRadar
          studentId={id}
          color={meta.color}
          title={`${meta.label} skill snapshot`}
        />
      </div>

      {/* DUPR Skill Assessment Panel — coach checklist for the player's current level */}
      {sport === "pickleball" && (
        <div id="assessment" style={{ scrollMarginTop: "80px" }}>
          <CoachAssessmentPanel studentId={id} onLevelChange={load} />
        </div>
      )}

      {/* Iter90 — Claim history breadcrumb. Self-hides when the player has
          never raised a claim. Rendered here so it sits next to the DUPR
          panel: level context + claim context in one glance. */}
      <ClaimHistoryBreadcrumb playerId={id} />

      {/* Coach journal — append-only timeline of observations, grouped by the
          skill level the player was at when each note was written. Sits between
          the assessment panel and the drill recommendations so coaches can
          cross-reference "what I wrote 3 months ago at 3.0" against today's
          rating. Only admins + coaches booked with this player see this. */}
      <CoachNotesTimeline studentId={id} defaultContext="general" />

      {/* Iter105 (Slice D) — Assessment history archive viewer. Collapsible so
          it stays out of the way when empty. Combines live entries (< 30 days)
          + archived entries (moved by the admin migration button). */}
      <AssessmentHistoryPanel studentId={id} />

      {/* Recap history — appears once the coach has sent at least one recap.
          Self-hides when empty. Lets the coach re-send an old recap in one tap
          (useful if the original email bounced or the player needs a nudge). */}
      <RecapHistory studentId={id} studentName={player?.name || ""} />

      {/* Iter156 — Post-session feedback log. Only entries this coach has
          written are returned (admin/the student see everything). Each row
          has Copy + Reuse-for-another-player actions. Self-hides when empty. */}
      <NoShowLog studentId={id} />
      <FeedbackHistoryPanel studentId={id} studentName={player?.name || ""} />

      {/* Iter168 — Combined purchase + credit + feedback view. Self-hides
          when the player has neither packs nor feedback yet. */}
      <PurchaseHistory studentId={id} studentName={player?.name || ""} />

      {/* Floating mic — bottom-right FAB so a coach walking off court can
          dictate without scrolling to the notes section. Only rendered for
          coach/admin (the timeline itself is coach-only anyway). */}
      {(user?.role === "coach" || user?.role === "admin" ||
        (user?.permissions || []).some((p) => p === "admin" || p === "super_admin")) && (
        <VoiceNoteRecorder
          studentId={id}
          studentName={player?.name || ""}
          variant="fab"
          defaultContext="session"
          onSaved={() => window.dispatchEvent(new CustomEvent("coach-notes:changed", { detail: { studentId: id } }))}
        />
      )}

      {/* Recommended drills — auto-queried from the drill library based on the
          player's weakest rated skills + a level window around their current
          assessment. Renders nothing if the coach hasn't rated any skills. */}
      <RecommendedDrills
        sport={sport}
        level={player?.assessment_level?.current}
        ratings={ratings}
        studentId={id}
        currentFocus={focus}
        onFocusAppended={(newFocus) => setFocus(newFocus)}
      />

      <MilestonesCard studentId={id} sport={sport} studentName={player.name} />

      {/* Iter75: Editorial Progress Map — coach view (student's professional pathway). */}
      {sport === "pickleball" && (
        <EditorialProgressMap studentId={id} sport={sport} />
      )}

      {/* Performance Packages — coach view of any active/past packs for this player.
          Hidden entirely when the player has no packs assigned (coaches manage packs
          via /packages; no need to nag on every player profile). */}
      {packages.length > 0 && (
        <div className="card-surface p-6 fade-up d3" data-testid="player-packages">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-[#CCFF00]" />
              <div className="label-mono">Performance Packages</div>
            </div>
            <Link to="/packages" className="text-xs uppercase tracking-wider text-[#CCFF00] hover:opacity-80 inline-flex items-center gap-1" data-testid="player-packages-manage">
              <Plus className="w-3.5 h-3.5" /> Manage packages
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {packages.map((p) => (
              <div key={p.id} className="bg-[#0A0A0A] border border-[#262626] p-3" data-testid={`player-pkg-${p.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="label-mono">{p.type === "monthly" ? "MONTHLY" : "BLOCK"} · {p.sport?.toUpperCase()}</div>
                  <div className="label-mono" style={{ color: p.status === "active" ? "#CCFF00" : "#A3A3A3" }}>{p.status?.toUpperCase()}</div>
                </div>
                <div className="flex items-baseline gap-2 mb-1">
                  <div className="h-display text-2xl" style={{ color: p.status === "active" ? "#CCFF00" : "#A3A3A3" }}>{p.sessions_remaining}</div>
                  <div className="label-mono text-[#A3A3A3]">/ {p.sessions_total}</div>
                </div>
                <div className="text-[10px] text-[#A3A3A3]">£{p.price} {p.paid ? "· paid" : "· unpaid"}{p.expires_at ? ` · exp ${String(p.expires_at).slice(0,10)}` : ""}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* History */}
      <div className="card-surface p-6 fade-up d3">
        <div className="label-mono mb-4">{meta.label} progression history</div>
        <div className="h-60">
          {historyChart.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={historyChart}>
                <XAxis dataKey="date" tick={{ fill: "#A3A3A3", fontSize: 10 }} stroke="#262626" />
                <YAxis domain={[0, 10]} tick={{ fill: "#A3A3A3", fontSize: 10 }} stroke="#262626" />
                <Tooltip contentStyle={{ background: "#141414", border: "1px solid #262626", borderRadius: 0 }} />
                <Line type="monotone" dataKey="avg" stroke={meta.color} strokeWidth={2} dot={{ fill: meta.color, r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <div className="text-sm text-[#A3A3A3]">Save a {meta.label.toLowerCase()} rating to start tracking progression.</div>}
        </div>
      </div>

      {(report || reports[0]) && (
        <div className="card-surface p-6 fade-up d4">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-4 h-4" style={{ color: meta.color }} />
            <div className="label-mono">Latest {meta.label} AI report</div>
          </div>
          {(() => {
            const r = report || reports[0];
            return (
              <div className="space-y-4 text-sm" data-testid="ai-report">
                <ReportBlock title="Strengths" body={r.strengths} color={meta.color} />
                <ReportBlock title="Weaknesses" body={r.weaknesses} color="#FF4500" />
                <ReportBlock title="Next focus" body={r.focus} color="#007AFF" />
                <ReportBlock title="Summary" body={r.summary} color="#A3A3A3" />
              </div>
            );
          })()}
        </div>
      )}

      {/* Cap-breach confirmation modal — fires when the coach tries to save
          ratings above the typical threshold for the player's current level.
          Mirrors the user's brief: "7 and above is a higher rated player who
          makes very few unforced errors in this skill area." */}
      {capModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="cap-confirm-modal">
          <div className="bg-[#0A0A0A] border-l-4 border-[#FF4500] w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="label-mono mb-2" style={{ color: "#FF4500" }}>RATING CHECK · ABOVE TYPICAL CAP</div>
            <h2 className="h-display text-2xl tracking-tight mb-3">Are you sure?</h2>
            <p className="text-sm text-[#A3A3A3] mb-4">
              The following ratings are above the typical ceiling for a
              <span className="text-white"> level {player?.assessment_level?.current || "—"} </span>
              player. Scores this high are usually reserved for players who
              <span className="text-white"> make very few unforced errors </span>
              in this skill area.
            </p>
            <ul className="space-y-2 mb-5 text-sm border border-[#262626] divide-y divide-[#262626]">
              {capModal.breaches.map((b) => (
                <li key={b.skill} className="flex items-center justify-between p-3" data-testid={`cap-breach-${b.skill.replace(/\s+/g,'')}`}>
                  <div>
                    <div className="text-white">{b.skill}</div>
                    <div className="text-[10px] text-[#A3A3A3]">Typical cap at this level · {b.cap}</div>
                  </div>
                  <div className="h-display text-3xl text-[#FF4500]">{b.rating}</div>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={capModal.onConfirm}
                className="btn-volt px-5 py-3 text-xs inline-flex items-center gap-2"
                style={{ background: "#FF4500", color: "#0A0A0A" }}
                data-testid="cap-confirm-save"
              >
                <Save className="w-4 h-4" /> Yes, save these ratings
              </button>
              <button
                onClick={() => setCapModal(null)}
                className="px-5 py-3 text-xs uppercase tracking-wider border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00]"
                data-testid="cap-confirm-cancel"
              >
                Go back and adjust
              </button>
            </div>
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}

function PlayerSnapshotLock({ player }) {
  const [status, setStatus] = useState(player.access_request_status || "none");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/players/${player.id}/access-requests`, { reason });
      setStatus(data.status || "pending");
      toast.success("Access request sent — an admin will review it shortly.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't submit request");
    } finally { setSubmitting(false); }
  };
  return (
    <div className="card-surface p-8 max-w-2xl fade-up" data-testid="player-snapshot-lock">
      <div className="flex items-center gap-2 label-mono text-[#FF4500] mb-4">
        <Lock className="w-3.5 h-3.5" /> LIMITED VIEW
      </div>
      <h1 className="h-display text-4xl tracking-tighter" data-testid="lock-player-name">{player.name}</h1>
      {player.assessment_level?.current && (
        <div className="mt-3 text-sm">
          <span className="text-[#A3A3A3]">Level: </span>
          <span className="text-[#CCFF00] text-2xl font-bold" data-testid="lock-player-level">{player.assessment_level.current}</span>
        </div>
      )}
      {player.safety_notes && (
        <div className="mt-4 border border-[#FF4500] p-3" data-testid="lock-player-safety-notes">
          <div className="label-mono text-[#FF4500] mb-1">SAFETY NOTES</div>
          <div className="text-sm text-white">{player.safety_notes}</div>
        </div>
      )}
      {typeof player.total_sessions === "number" && (
        <div className="mt-3 text-sm" data-testid="lock-total-sessions">
          <span className="text-[#A3A3A3]">Total sessions: </span>
          <span className="text-white font-bold">{player.total_sessions}</span>
        </div>
      )}
      <p className="text-xs text-[#A3A3A3] leading-relaxed mt-6 max-w-md">
        {player.restrict_sharing
          ? "Access Restricted. This player has enabled restricted sharing — full details are visible only to their approved coaches. Request access below."
          : "This player has activated private mode. Full profile details are visible only to coaches on their approved list. Request access below if you need to run assessments or view session history."}
      </p>
      {status === "pending" ? (
        <div
          className="mt-6 inline-flex items-center gap-2 text-xs px-3 py-2 border border-[#CCFF00] text-[#CCFF00]"
          data-testid="lock-request-pending"
        >
          <Clock className="w-3.5 h-3.5" /> ACCESS REQUESTED — AWAITING APPROVAL
        </div>
      ) : (
        <div className="mt-6 space-y-2">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            maxLength={400}
            placeholder="Optional — a short reason helps the reviewer decide faster."
            className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 text-xs focus:border-[#CCFF00] focus:outline-none"
            data-testid="lock-request-reason"
          />
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="btn-volt px-4 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="lock-request-submit"
          >
            <Lock className="w-3.5 h-3.5" /> {submitting ? "Sending…" : "Request Full Access"}
          </button>
        </div>
      )}
    </div>
  );
}

function ReportBlock({ title, body, color }) {
  return (
    <div className="border-l-2 pl-4" style={{ borderColor: color }}>
      <div className="label-mono mb-1" style={{ color }}>{title}</div>
      <div className="text-[#E5E5E5] leading-relaxed">{body || "—"}</div>
    </div>
  );
}

// Iter102 — PrivacyAccessCard: admin-only controls on a Player profile.
// Two levers: (1) Restrict Sharing toggle — flips the player's flag so any
// Global-Access coach sees only Name/Level/Total-Sessions/Safety-Notes.
// (2) Grant Access override — admin adds a specific coach to
// allowed_coach_ids without going through the request/approval loop.
function PrivacyAccessCard({ player, onChanged }) {
  const [candidates, setCandidates] = useState([]);
  const [pickedCoach, setPickedCoach] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!pickerOpen || candidates.length) return;
    api.get("/coach/team-access/candidates")
      .then((r) => setCandidates(r.data || []))
      .catch(() => {});
  }, [pickerOpen, candidates.length]);
  const toggleRestrict = async () => {
    const next = !player.restrict_sharing;
    if (!window.confirm(next
      ? `Enable Restrict Sharing for ${player.name}?\n\nGlobal coaches will only see Name, Level, Total Sessions, and Safety Notes. Everyone else keeps their existing access.`
      : `Disable Restrict Sharing for ${player.name}?\n\nGlobal coaches will regain their normal Discovery view of this player.`)) return;
    setSaving(true);
    try {
      await api.put(`/players/${player.id}/restrict-sharing`, { enabled: next });
      toast.success(next ? "Restrict Sharing ON" : "Restrict Sharing OFF");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  const grantAccess = async () => {
    if (!pickedCoach) return;
    setSaving(true);
    try {
      await api.post(`/admin/players/${player.id}/grant-access`, { coach_id: pickedCoach });
      const c = candidates.find((x) => x.id === pickedCoach);
      toast.success(`${c?.name || "Coach"} granted full access`);
      setPickedCoach("");
      setPickerOpen(false);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="card-surface p-5 space-y-4 fade-up" data-testid="privacy-access-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <div className="label-mono text-[#CCFF00] inline-flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> PRIVACY & ACCESS</div>
          <div className="text-xs text-[#A3A3A3] mt-1">
            Restrict Sharing hides personal details from Global-Access coaches. Admins can grant a specific coach full access below.
          </div>
        </div>
        <button
          type="button"
          onClick={toggleRestrict}
          disabled={saving}
          data-testid="restrict-sharing-toggle"
          className="px-3 py-2 text-xs uppercase tracking-wider font-bold border transition-colors disabled:opacity-50"
          style={player.restrict_sharing
            ? { borderColor: "#FF4500", color: "#FF4500", background: "rgba(255,69,0,0.05)" }
            : { borderColor: "#525252", color: "#A3A3A3" }}
        >
          {player.restrict_sharing ? "Restrict Sharing ON" : "+ Restrict Sharing"}
        </button>
      </div>
      <div className="pt-3 border-t border-[#262626] space-y-2">
        {!pickerOpen ? (
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            data-testid="admin-grant-access-open"
            className="text-xs px-3 py-2 border border-[#CCFF00] text-[#CCFF00] inline-flex items-center gap-1"
          >
            + Grant a coach full access (bypass allowlist)
          </button>
        ) : (
          <div className="space-y-2" data-testid="admin-grant-access-picker">
            <div className="label-mono">Pick coach to grant full access</div>
            <select
              value={pickedCoach}
              onChange={(e) => setPickedCoach(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 text-sm focus:border-[#CCFF00] focus:outline-none"
              data-testid="admin-grant-access-select"
            >
              <option value="">— select coach —</option>
              {candidates.map((c) => (
                <option key={c.id} value={c.id}>{c.name} · {c.email}</option>
              ))}
            </select>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={grantAccess}
                disabled={!pickedCoach || saving}
                data-testid="admin-grant-access-submit"
                className="btn-volt px-3 py-2 text-xs disabled:opacity-50"
              >{saving ? "Granting…" : "Grant access"}</button>
              <button
                type="button"
                onClick={() => { setPickerOpen(false); setPickedCoach(""); }}
                className="text-xs px-3 py-2 border border-[#262626] text-[#A3A3A3] hover:text-white"
                data-testid="admin-grant-access-cancel"
              >Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Iter105 (Slice D) — Assessment history archive panel. Shows the combined
// live + archived timeline of assessment_level.history entries so the main
// player profile can stay lean. Self-hides when there's nothing to show.
// ---------------------------------------------------------------------------
function AssessmentHistoryPanel({ studentId }) {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [live, setLive] = useState([]);
  const [archived, setArchived] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get(`/coach/players/${studentId}/assessment-history`)
      .then((r) => {
        if (cancelled) return;
        setLive(Array.isArray(r.data?.live) ? r.data.live : []);
        setArchived(Array.isArray(r.data?.archived) ? r.data.archived : []);
        setLoaded(true);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(formatApiError(e.response?.data?.detail) || "");
        setLoaded(true);
      });
    return () => { cancelled = true; };
  }, [studentId]);

  if (!loaded) return null;
  const total = live.length + archived.length;
  if (total === 0 && !error) return null;

  const fmt = (row) => {
    const when = row.date || row.at || row.adjusted_at || row.completed_at || row.created_at || row.migrated_at || "";
    const level = row.level || row.new_level || row.to_level || row.adjusted_to || "";
    const from = row.from_level || row.prev_level || row.level || "";
    const note = row.note || row.comment || row.reason || "";
    const author = row.by || row.author || row.updated_by_name || row.adjusted_by_name || "";
    return { when, level, from, note, author };
  };

  return (
    <div className="card-surface p-5 fade-up d2" data-testid="assessment-history-panel">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="w-full flex items-center justify-between gap-3 text-left"
        data-testid="assessment-history-toggle"
      >
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-[#A3A3A3]" />
          <div className="label-mono">Assessment history</div>
          <span className="text-[10px] text-[#525252] uppercase tracking-wider">
            {live.length} live · {archived.length} archived
          </span>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-[#A3A3A3]" /> : <ChevronRight className="w-4 h-4 text-[#A3A3A3]" />}
      </button>
      {open && (
        <div className="mt-4 space-y-3">
          {error && <div className="text-xs text-[#FF4500]" data-testid="assessment-history-error">{error}</div>}
          {live.length > 0 && (
            <div data-testid="assessment-history-live">
              <div className="label-mono text-[10px] text-[#CCFF00] mb-1">Recent (kept on profile)</div>
              <ol className="space-y-1.5">
                {live.slice(0, 20).map((row, i) => {
                  const f = fmt(row);
                  return (
                    <li key={`live-${i}`} className="text-xs text-[#E5E5E5] border-l-2 border-[#CCFF00] pl-3 py-1">
                      <div className="text-[10px] text-[#A3A3A3] font-mono">
                        {f.when?.slice(0, 10) || "—"}
                        {f.author ? ` · ${f.author}` : ""}
                        {(f.from || f.level) ? ` · ${f.from || "?"} → ${f.level || "?"}` : ""}
                      </div>
                      {f.note && <div className="mt-0.5">{f.note}</div>}
                    </li>
                  );
                })}
              </ol>
            </div>
          )}
          {archived.length > 0 && (
            <div data-testid="assessment-history-archived">
              <div className="label-mono text-[10px] text-[#A3A3A3] mb-1">Archived (migrated by admin)</div>
              <ol className="space-y-1.5 opacity-80">
                {archived.slice(0, 50).map((row, i) => {
                  const f = fmt(row);
                  return (
                    <li key={`arch-${i}`} className="text-xs text-[#A3A3A3] border-l-2 border-[#262626] pl-3 py-1">
                      <div className="text-[10px] text-[#525252] font-mono">
                        {f.when?.slice(0, 10) || "—"}
                        {f.author ? ` · ${f.author}` : ""}
                        {(f.from || f.level) ? ` · ${f.from || "?"} → ${f.level || "?"}` : ""}
                      </div>
                      {f.note && <div className="mt-0.5">{f.note}</div>}
                    </li>
                  );
                })}
              </ol>
            </div>
          )}
          {live.length === 0 && archived.length === 0 && !error && (
            <div className="text-xs text-[#525252] italic">No assessment history yet.</div>
          )}
        </div>
      )}
    </div>
  );
}


function PlayerContactDetails({ pd }) {
  if (!pd || !Object.keys(pd).length) return null;
  const addr = [pd.address_line1, pd.address_line2, pd.city, pd.postcode].filter(Boolean).join(", ");
  const dob = pd.dob ? new Date(`${String(pd.dob).slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null;
  return (
    <div className="mt-2 text-xs text-[#A3A3A3] space-y-0.5" data-testid="player-contact-details">
      {pd.phone && <div data-testid="player-contact-phone">Phone: <span className="text-white">{pd.phone}</span></div>}
      {dob && <div data-testid="player-contact-dob">Date of birth: <span className="text-white">{dob}</span></div>}
      {addr && <div data-testid="player-contact-address">Address: <span className="text-white">{addr}</span></div>}
    </div>
  );
}
